import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  createBlogPost,
  updateBlogPost,
  fetchBlogPostById,
  fetchBlogFilters,
  clearBlogDetail,
} from '../../../Redux/Actions/actions';
import {
  BLOG_LIMITS,
  BLOG_CATEGORIES,
  BLOG_STATUSES,
  ALLOWED_MEDIA_MIMES,
  ALLOWED_COVER_MIMES,
  BLOCKED_MIMES,
  ALLOWED_MEDIA_LABEL,
  bytesToMB,
} from '../../../constants/blog';
import BlogHelpPanel from './BlogHelpPanel';
import styles from './BlogForm.module.css';

const allowedBlog = [
  'arosa@byraices.com',
  'tsanesteban@byraices.com',
  'dsanesteban@byraices.com',
  'jgarcia@byraices.com',
];

const emptyForm = {
  title: '',
  summary: '',
  body: '',
  category: 'novedades',
  tags: '',
  status: 'borrador',
  featured: false,
  authorName: '',
  officeId: '',
  franquiciaId: '',
};

const BlogForm = () => {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const detail = useSelector((state) => state.blogDetail);
  const backendFilters = useSelector((state) => state.blogFilters);
  const user = useSelector((state) => state.user);

  const canManage = user?.user?.mail && allowedBlog.includes(user.user.mail);

  const [form, setForm] = useState(emptyForm);
  const [coverFile, setCoverFile] = useState(null);
  const [removeCover, setRemoveCover] = useState(false);
  const [mediaItems, setMediaItems] = useState([]); // { file, title }
  const [existingMedia, setExistingMedia] = useState([]); // media[] del post en edición
  const [removeMediaIds, setRemoveMediaIds] = useState([]);
  const [fieldErrors, setFieldErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [progress, setProgress] = useState(0);

  const coverInputRef = useRef(null);
  const mediaInputRef = useRef(null);

  const offices = backendFilters?.offices || [];
  const categories = backendFilters?.categories?.length
    ? backendFilters.categories
    : BLOG_CATEGORIES;

  // Redirige si el usuario no tiene permiso de carga.
  useEffect(() => {
    if (user && !canManage) {
      toast.error('No tenés permiso para crear publicaciones.');
      navigate('/blog');
    }
  }, [user, canManage, navigate]);

  useEffect(() => {
    dispatch(fetchBlogFilters());
    if (isEdit) dispatch(fetchBlogPostById(id));
    return () => {
      if (isEdit) dispatch(clearBlogDetail());
    };
  }, [dispatch, id, isEdit]);

  // Precarga de datos en modo edición.
  useEffect(() => {
    if (isEdit && detail && String(detail.id) === String(id)) {
      setForm({
        title: detail.title || '',
        summary: detail.summary || '',
        body: detail.body || '',
        category: detail.category || 'novedades',
        tags: Array.isArray(detail.tags) ? detail.tags.join(', ') : '',
        status: detail.status || 'borrador',
        featured: Boolean(detail.featured),
        authorName: detail.authorName || '',
        officeId: detail.officeId || '',
        franquiciaId: detail.franquiciaId || '',
      });
      setExistingMedia(Array.isArray(detail.media) ? detail.media : []);
      setRemoveMediaIds([]);
      setRemoveCover(false);
    }
  }, [isEdit, detail, id]);

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  // Validación de un archivo individual (tipo + tamaño).
  const validateFile = (file, allowedMimes) => {
    if (BLOCKED_MIMES.includes(file.type)) {
      return 'PowerPoint no se acepta. Exportá el archivo a PDF antes de subirlo.';
    }
    if (allowedMimes && !allowedMimes.includes(file.type)) {
      return `Tipo de archivo no permitido (${file.type || 'desconocido'}).`;
    }
    if (file.size > BLOG_LIMITS.MAX_FILE_SIZE_BYTES) {
      return `"${file.name}" pesa ${bytesToMB(file.size)} MB (máximo 150 MB).`;
    }
    return null;
  };

  const handleCoverSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const err = validateFile(file, ALLOWED_COVER_MIMES);
    if (err) {
      toast.error(err);
      e.target.value = '';
      return;
    }
    setCoverFile(file);
    setRemoveCover(false);
  };

  const currentMediaCount = useMemo(
    () => existingMedia.length - removeMediaIds.length + mediaItems.length,
    [existingMedia, removeMediaIds, mediaItems]
  );

  const handleMediaSelect = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    const accepted = [];
    for (const file of files) {
      const err = validateFile(file, ALLOWED_MEDIA_MIMES);
      if (err) {
        toast.error(err);
        continue;
      }
      accepted.push({ file, title: '' });
    }

    const total = currentMediaCount + accepted.length;
    if (total > BLOG_LIMITS.MAX_MEDIA_FILES) {
      toast.error(`Máximo ${BLOG_LIMITS.MAX_MEDIA_FILES} archivos por publicación.`);
      e.target.value = '';
      return;
    }

    setMediaItems((prev) => [...prev, ...accepted]);
    e.target.value = '';
  };

  const removeNewMedia = (index) => {
    setMediaItems((prev) => prev.filter((_, i) => i !== index));
  };

  const setNewMediaTitle = (index, title) => {
    setMediaItems((prev) =>
      prev.map((m, i) => (i === index ? { ...m, title } : m))
    );
  };

  const toggleRemoveExisting = (mediaId) => {
    setRemoveMediaIds((prev) =>
      prev.includes(mediaId)
        ? prev.filter((x) => x !== mediaId)
        : [...prev, mediaId]
    );
  };

  const validateForm = () => {
    const errs = {};
    if (!form.title.trim()) errs.title = 'El título es obligatorio.';
    if (form.summary.length > BLOG_LIMITS.SUMMARY_MAX_LENGTH) {
      errs.summary = `El resumen no puede superar ${BLOG_LIMITS.SUMMARY_MAX_LENGTH} caracteres.`;
    }
    setFieldErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const buildFormData = () => {
    const fd = new FormData();
    fd.append('title', form.title.trim());
    fd.append('summary', form.summary);
    fd.append('body', form.body);
    fd.append('category', form.category);
    fd.append('status', form.status);
    fd.append('featured', String(form.featured));
    if (form.authorName) fd.append('authorName', form.authorName);
    if (form.officeId) fd.append('officeId', String(form.officeId));
    if (form.franquiciaId) fd.append('franquiciaId', String(form.franquiciaId));

    const tagsArr = form.tags
      .split(',')
      .map((t) => t.trim())
      .filter(Boolean);
    fd.append('tags', JSON.stringify(tagsArr));

    // Títulos por archivo, en el mismo orden que media[].
    const mediaTitles = mediaItems.map((m) => m.title || '');
    fd.append('mediaTitles', JSON.stringify(mediaTitles));
    mediaItems.forEach((m) => fd.append('media', m.file));

    if (coverFile) fd.append('cover', coverFile);

    if (isEdit) {
      if (removeMediaIds.length) {
        fd.append('removeMediaIds', JSON.stringify(removeMediaIds));
      }
      if (removeCover) fd.append('removeCover', 'true');
    }
    return fd;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (!validateForm()) {
      toast.error('Revisá los campos marcados.');
      return;
    }

    setSubmitting(true);
    setProgress(0);
    const fd = buildFormData();
    const options = {
      onUploadProgress: (evt) => {
        if (evt.total) {
          setProgress(Math.round((evt.loaded / evt.total) * 100));
        }
      },
    };

    const res = isEdit
      ? await dispatch(updateBlogPost(id, fd, options))
      : await dispatch(createBlogPost(fd, options));

    setSubmitting(false);

    if (res?.success) {
      toast.success(
        res.message || (isEdit ? 'Publicación actualizada.' : 'Publicación creada.')
      );
      const newId = res.data?.id || id;
      navigate(newId ? `/blog/${newId}` : '/blog');
    } else {
      // Errores de campo del backend (envelope estándar).
      if (res?.fieldErrors && Object.keys(res.fieldErrors).length) {
        setFieldErrors((prev) => ({ ...prev, ...res.fieldErrors }));
      }
      toast.error(res?.message || 'No se pudo guardar la publicación.');
    }
  };

  if (!canManage) return null;

  return (
    <div className={styles.container}>
      <form className={styles.form} onSubmit={handleSubmit}>
        <div className={styles.formHeader}>
          <h1 className={styles.title}>
            {isEdit ? 'Editar publicación' : 'Nueva publicación'}
          </h1>
          <button
            type="button"
            className={styles.backBtn}
            onClick={() => navigate('/blog')}
          >
            ← Volver
          </button>
        </div>

        <BlogHelpPanel />

        {/* Título */}
        <div className={styles.field}>
          <label className={styles.label}>Título *</label>
          <input
            type="text"
            className={styles.input}
            value={form.title}
            onChange={(e) => handleChange('title', e.target.value)}
            placeholder="Ej.: Informe de mercado Q1 2026"
          />
          {fieldErrors.title && <span className={styles.fieldError}>{fieldErrors.title}</span>}
        </div>

        {/* Resumen */}
        <div className={styles.field}>
          <label className={styles.label}>
            Resumen ({form.summary.length}/{BLOG_LIMITS.SUMMARY_MAX_LENGTH})
          </label>
          <textarea
            className={styles.textarea}
            rows={2}
            maxLength={BLOG_LIMITS.SUMMARY_MAX_LENGTH}
            value={form.summary}
            onChange={(e) => handleChange('summary', e.target.value)}
            placeholder="Texto corto que se muestra en la card del feed."
          />
          {fieldErrors.summary && <span className={styles.fieldError}>{fieldErrors.summary}</span>}
        </div>

        {/* Descripción / body */}
        <div className={styles.field}>
          <label className={styles.label}>Descripción (admite HTML)</label>
          <textarea
            className={styles.textarea}
            rows={6}
            value={form.body}
            onChange={(e) => handleChange('body', e.target.value)}
            placeholder="Contenido completo de la publicación."
          />
        </div>

        {/* Clasificación */}
        <div className={styles.row3}>
          <div className={styles.field}>
            <label className={styles.label}>Categoría</label>
            <select
              className={styles.select}
              value={form.category}
              onChange={(e) => handleChange('category', e.target.value)}
            >
              {categories.map((c) => (
                <option key={c.value} value={c.value}>
                  {c.label}
                </option>
              ))}
            </select>
          </div>
          <div className={styles.field}>
            <label className={styles.label}>Estado</label>
            <select
              className={styles.select}
              value={form.status}
              onChange={(e) => handleChange('status', e.target.value)}
            >
              {BLOG_STATUSES.map((s) => (
                <option key={s.value} value={s.value}>
                  {s.label}
                </option>
              ))}
            </select>
          </div>
          <div className={styles.field}>
            <label className={styles.label}>Destacado</label>
            <label className={styles.checkboxRow}>
              <input
                type="checkbox"
                checked={form.featured}
                onChange={(e) => handleChange('featured', e.target.checked)}
              />
              <span>Fijar arriba del feed</span>
            </label>
          </div>
        </div>

        {/* Autor / oficina */}
        <div className={styles.row3}>
          <div className={styles.field}>
            <label className={styles.label}>Autor (opcional)</label>
            <input
              type="text"
              className={styles.input}
              value={form.authorName}
              onChange={(e) => handleChange('authorName', e.target.value)}
            />
          </div>
          <div className={styles.field}>
            <label className={styles.label}>Oficina (opcional)</label>
            {offices.length > 0 ? (
              <select
                className={styles.select}
                value={form.officeId}
                onChange={(e) => handleChange('officeId', e.target.value)}
              >
                <option value="">—</option>
                {offices.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.name}
                  </option>
                ))}
              </select>
            ) : (
              <input
                type="number"
                className={styles.input}
                value={form.officeId}
                onChange={(e) => handleChange('officeId', e.target.value)}
              />
            )}
          </div>
          <div className={styles.field}>
            <label className={styles.label}>Etiquetas (separadas por coma)</label>
            <input
              type="text"
              className={styles.input}
              value={form.tags}
              onChange={(e) => handleChange('tags', e.target.value)}
              placeholder="inversión, 2026"
            />
          </div>
        </div>

        {/* Portada */}
        <div className={styles.field}>
          <label className={styles.label}>Portada (imagen para la card)</label>
          {isEdit && detail?.coverImage && !coverFile && !removeCover && (
            <div className={styles.coverPreview}>
              <img src={detail.coverImage} alt="Portada actual" />
              <label className={styles.checkboxRow}>
                <input
                  type="checkbox"
                  checked={removeCover}
                  onChange={(e) => setRemoveCover(e.target.checked)}
                />
                <span>Quitar portada</span>
              </label>
            </div>
          )}
          {coverFile && (
            <div className={styles.selectedFile}>
              {coverFile.name}
              <button
                type="button"
                className={styles.removeChip}
                onClick={() => {
                  setCoverFile(null);
                  if (coverInputRef.current) coverInputRef.current.value = '';
                }}
              >
                ✕
              </button>
            </div>
          )}
          <input
            ref={coverInputRef}
            type="file"
            accept={ALLOWED_COVER_MIMES.join(',')}
            className={styles.fileInput}
            onChange={handleCoverSelect}
          />
        </div>

        {/* Media */}
        <div className={styles.field}>
          <label className={styles.label}>
            Archivos ({currentMediaCount}/{BLOG_LIMITS.MAX_MEDIA_FILES}) — {ALLOWED_MEDIA_LABEL}
          </label>

          {/* Existentes (modo edición) */}
          {isEdit && existingMedia.length > 0 && (
            <div className={styles.mediaList}>
              {existingMedia.map((m) => {
                const marked = removeMediaIds.includes(m.id);
                return (
                  <div
                    key={m.id}
                    className={`${styles.mediaRow} ${marked ? styles.mediaRemoved : ''}`}
                  >
                    <span className={styles.mediaType}>{m.type}</span>
                    <span className={styles.mediaName}>{m.title || m.filename}</span>
                    <label className={styles.checkboxRow}>
                      <input
                        type="checkbox"
                        checked={marked}
                        onChange={() => toggleRemoveExisting(m.id)}
                      />
                      <span>Quitar</span>
                    </label>
                  </div>
                );
              })}
            </div>
          )}

          {/* Nuevos archivos */}
          {mediaItems.length > 0 && (
            <div className={styles.mediaList}>
              {mediaItems.map((m, i) => (
                <div key={i} className={styles.mediaRowNew}>
                  <span className={styles.mediaName}>{m.file.name}</span>
                  <input
                    type="text"
                    className={styles.mediaTitleInput}
                    placeholder="Título del archivo (opcional)"
                    value={m.title}
                    onChange={(e) => setNewMediaTitle(i, e.target.value)}
                  />
                  <button
                    type="button"
                    className={styles.removeChip}
                    onClick={() => removeNewMedia(i)}
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>
          )}

          <input
            ref={mediaInputRef}
            type="file"
            multiple
            accept={ALLOWED_MEDIA_MIMES.join(',')}
            className={styles.fileInput}
            onChange={handleMediaSelect}
          />
          <span className={styles.hint}>
            Máx. 150 MB por archivo. PowerPoint no se acepta (exportá a PDF).
          </span>
        </div>

        {/* Progreso */}
        {submitting && (
          <div className={styles.progressWrap}>
            <div className={styles.progressBar} style={{ width: `${progress}%` }} />
            <span className={styles.progressText}>Subiendo… {progress}%</span>
          </div>
        )}

        <div className={styles.actions}>
          <button type="submit" className={styles.submitBtn} disabled={submitting}>
            {submitting
              ? 'Guardando…'
              : isEdit
              ? 'Guardar cambios'
              : 'Publicar'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default BlogForm;
